import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { addDays } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import type { PlanJob } from '../data/plan.ts'
import { buildHistory } from '../data/settingsHistory.ts'
import { ruleSettings, settingsWarnings, type SettingsWarning } from '../data/settingsRule.ts'
import { warningText, type T } from '../pages/plan/settingsText.ts'
import { useReadsWork, useSettingsData, useToday } from '../pages/plan/useSettingsData.ts'

const NONE: ReadonlyMap<string, SettingsWarning[]> = new Map()

/** M25-A (docs/M25.md D, "The board and the tracking page"; rule 2): THE WARNINGS of a set of jobs, by job id — a planned job (today or later) whose DIRECTION or
 *  PATTERN repeats the previous cut on one of its areas (a finished cut, or one planned before it). Never the groomers, never a lap, never a refusal: a line
 *  of information (rule 7). Computed from the device's own copy and only where the viewer holds `job.plan` AND `worklog.read` and is not on a workstation: on a
 *  crew phone, the mechanic's or the workstation the map is empty and the card shows nothing (no device reads other people's finished work as planned). */
export function useSettingsWarnings(jobs: readonly PlanJob[]): ReadonlyMap<string, SettingsWarning[]> {
  const { i18n } = useTranslation()
  const { site } = useClub()
  const reads = useReadsWork()
  const data = useSettingsData()
  const today = useToday()
  const lang = i18n.language
  return useMemo(() => {
    if (!reads || !data) return NONE
    const byType = new Map<string, PlanJob[]>()
    for (const j of jobs) if (j.state === 'planned' && j.scheduled_local_date >= today) byType.set(j.task_type_code, [...(byType.get(j.task_type_code) ?? []), j])
    const out = new Map<string, SettingsWarning[]>()
    for (const [code, list] of byType) {
      const tt = data.taskTypes.find((x) => x.code === code)
      const settings = ruleSettings(tt, lang)
      if (settings.length === 0) continue
      const areaIds = [...new Set(list.flatMap((j) => j.areaIds))]
      const until = addDays(list.reduce((m, j) => (j.scheduled_local_date > m ? j.scheduled_local_date : m), list[0]!.scheduled_local_date), 1)
      const history = buildHistory({
        tt, jobs: data.jobs, jobAreas: data.jobAreas, completions: data.completions, coverage: data.coverage, confirmations: data.confirmations,
        areaIds, today, until, tz: site.timezone, readsWork: true,
      })
      for (const w of settingsWarnings(list.map((j) => ({ id: j.id, day: j.scheduled_local_date, areaIds: j.areaIds, params: j.params })), history, { readsWork: true, settings })) out.set(w.jobId, [...(out.get(w.jobId) ?? []), w])
    }
    return out
  }, [reads, data, jobs, today, lang, site.timezone])
}

/** The sand line on a day card (and the tracking page): « jeu. 30 : même axe qu'au dernier passage (fait le mar. 28) », one 56-px link to `/plan/settings` at the
 *  job's day. Nothing without a warning. */
export function SettingsWarningLine({ warnings, testId = 'settings-warning', className = '' }: { warnings: readonly SettingsWarning[]; testId?: string; className?: string }) {
  const { t, i18n } = useTranslation()
  if (warnings.length === 0) return null
  const first = warnings[0]!
  const more = warnings.length - 1
  return (
    <Link to="/plan/settings" search={{ d: first.day } as never} data-testid={testId}
      className={`flex min-h-14 flex-wrap items-center gap-x-1 rounded-xl bg-sand px-3 py-1 text-sm font-semibold text-sand-ink dark:bg-night-line dark:text-sand ${className}`}>
      <span>{warningText(t as T, first, i18n.language)}</span>
      {more > 0 && <span>{' '}{t('plan.settingsWarning.more', { count: more })}</span>}
    </Link>
  )
}
